import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiRefreshCw,
  FiSave,
  FiSettings,
  FiBook,
  FiMessageSquare,
  FiPlus,
  FiTrash2,
  FiX,
  FiZap,
  FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import AdminSidebar from './AdminSidebar';
import DashboardHeader from './DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import {
  adminSupportAPI,
  OpenAIStatus,
  SupportBotConfigAdmin,
  SupportConversation,
  SupportKnowledgeArticle,
} from '../services/api';
import './MockCreditManagement.css';
import './MockCreditPanel.css';
import './MaterialManagement.css';
import './Dashboard.css';
import './SupportAIManagement.css';

interface SupportAIManagementProps {
  onLogout?: () => void;
  userEmail?: string;
}

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'mock_credits', label: 'Mock credits' },
  { value: 'payments', label: 'Payments' },
  { value: 'practice', label: 'Practice & materials' },
  { value: 'technical', label: 'Technical' },
];

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso || '—';
  }
}

const SupportAIManagement: React.FC<SupportAIManagementProps> = ({ onLogout, userEmail }) => {
  const { isCollapsed } = useSidebar();
  const [activeTab, setActiveTab] = useState<'settings' | 'knowledge' | 'conversations'>('settings');
  const [config, setConfig] = useState<SupportBotConfigAdmin | null>(null);
  const [configForm, setConfigForm] = useState<Partial<SupportBotConfigAdmin>>({});
  const [articles, setArticles] = useState<SupportKnowledgeArticle[]>([]);
  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [selectedConv, setSelectedConv] = useState<SupportConversation | null>(null);
  const [editingArticle, setEditingArticle] = useState<SupportKnowledgeArticle | null>(null);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [articleForm, setArticleForm] = useState({
    title: '',
    content: '',
    category: 'general',
    keywords: '',
    sort_order: '0',
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [convStatusFilter, setConvStatusFilter] = useState('');
  const [convSearch, setConvSearch] = useState('');
  const [knowledgeSearch, setKnowledgeSearch] = useState('');
  const [openaiStatus, setOpenaiStatus] = useState<OpenAIStatus | null>(null);
  const [openaiLoading, setOpenaiLoading] = useState(false);

  const loadOpenAIStatus = useCallback(async () => {
    try {
      setOpenaiLoading(true);
      const data = await adminSupportAPI.getOpenAIStatus();
      setOpenaiStatus(data);
    } catch (err: unknown) {
      setOpenaiStatus({
        key_configured: false,
        connected: false,
        message: err instanceof Error ? err.message : 'Could not check OpenAI connection',
        chat_models: [],
        embedding_models: [],
      });
    } finally {
      setOpenaiLoading(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const data = await adminSupportAPI.getConfig();
      setConfig(data);
      setConfigForm(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load config');
    }
  }, []);

  const loadKnowledge = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminSupportAPI.listKnowledge({
        q: knowledgeSearch.trim() || undefined,
      });
      setArticles(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load knowledge');
    } finally {
      setLoading(false);
    }
  }, [knowledgeSearch]);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminSupportAPI.listConversations({
        status: convStatusFilter || undefined,
        q: convSearch.trim() || undefined,
      });
      setConversations(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [convStatusFilter, convSearch]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (activeTab === 'settings') {
      loadOpenAIStatus();
    }
    if (activeTab === 'knowledge') loadKnowledge();
    if (activeTab === 'conversations') loadConversations();
  }, [activeTab, loadKnowledge, loadConversations, loadOpenAIStatus]);

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      const updated = await adminSupportAPI.updateConfig(configForm);
      setConfig(updated);
      setConfigForm(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const handleReindexAll = async () => {
    try {
      setReindexing(true);
      setError(null);
      const result = await adminSupportAPI.reindexAll();
      if (result.errors.length) {
        setError(`Indexed ${result.indexed}/${result.total}. Some articles failed — check OpenAI key.`);
      }
      await loadKnowledge();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Reindex failed');
    } finally {
      setReindexing(false);
    }
  };

  const openCreateArticle = () => {
    setEditingArticle(null);
    setArticleForm({
      title: '',
      content: '',
      category: 'general',
      keywords: '',
      sort_order: '0',
      is_active: true,
    });
    setShowArticleForm(true);
  };

  const openEditArticle = (art: SupportKnowledgeArticle) => {
    setEditingArticle(art);
    setArticleForm({
      title: art.title,
      content: art.content,
      category: art.category,
      keywords: art.keywords,
      sort_order: String(art.sort_order),
      is_active: art.is_active,
    });
    setShowArticleForm(true);
  };

  const handleSaveArticle = async () => {
    if (!articleForm.title.trim() || !articleForm.content.trim()) {
      setError('Title and content are required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload = {
        title: articleForm.title.trim(),
        content: articleForm.content.trim(),
        category: articleForm.category,
        keywords: articleForm.keywords.trim(),
        sort_order: parseInt(articleForm.sort_order, 10) || 0,
        is_active: articleForm.is_active,
        refresh_embedding: true,
      };
      if (editingArticle) {
        await adminSupportAPI.updateKnowledge(editingArticle.id, payload);
      } else {
        await adminSupportAPI.createKnowledge(payload);
      }
      setShowArticleForm(false);
      await loadKnowledge();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArticle = async (id: number) => {
    if (!window.confirm('Delete this knowledge article?')) return;
    try {
      await adminSupportAPI.deleteKnowledge(id);
      await loadKnowledge();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleRefreshEmbedding = async (id: number) => {
    try {
      await adminSupportAPI.refreshEmbedding(id);
      await loadKnowledge();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Embedding refresh failed');
    }
  };

  const openConversation = async (id: number) => {
    try {
      const conv = await adminSupportAPI.getConversation(id);
      setSelectedConv(conv);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load conversation');
    }
  };

  const handleCloseConversation = async (id: number) => {
    try {
      await adminSupportAPI.closeConversation(id);
      setSelectedConv(null);
      await loadConversations();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to close conversation');
    }
  };

  const chatModelOptions = (() => {
    const models = openaiStatus?.chat_models ?? [];
    const current = configForm.model_name;
    if (current && !models.includes(current)) {
      return [current, ...models];
    }
    return models;
  })();

  const embeddingModelOptions = (() => {
    const models = openaiStatus?.embedding_models ?? [];
    const current = configForm.embedding_model;
    if (current && !models.includes(current)) {
      return [current, ...models];
    }
    return models;
  })();

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <AdminSidebar />
      <div className="dashboard-main">
        <DashboardHeader userEmail={userEmail || 'admin'} organization="NAATI Admin" onLogout={onLogout} />
        <div className="dashboard-content">
          <div className="dashboard-content-inner">
            <motion.div
              className="dashboard-welcome"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <div>
                <h1 className="dashboard-title">NAATI AI</h1>
                <p className="dashboard-subtitle">
                  Configure the support chatbot, knowledge base, and review conversations.
                </p>
              </div>
            </motion.div>

            <div className="mock-credit-admin naati-ai-admin">
              <div className="material-tabs">
                <button
                  type="button"
                  className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
                  onClick={() => setActiveTab('settings')}
                >
                  {React.createElement(FiSettings as React.ComponentType<{ size?: number }>, { size: 18 })}
                  Settings
                </button>
                <button
                  type="button"
                  className={`tab-button ${activeTab === 'knowledge' ? 'active' : ''}`}
                  onClick={() => setActiveTab('knowledge')}
                >
                  {React.createElement(FiBook as React.ComponentType<{ size?: number }>, { size: 18 })}
                  Knowledge Base
                </button>
                <button
                  type="button"
                  className={`tab-button ${activeTab === 'conversations' ? 'active' : ''}`}
                  onClick={() => setActiveTab('conversations')}
                >
                  {React.createElement(FiMessageSquare as React.ComponentType<{ size?: number }>, { size: 18 })}
                  Conversations
                </button>
              </div>

              {error ? <div className="mock-credits-alert mock-credits-alert-warn">{error}</div> : null}

              {activeTab === 'settings' && (
                config ? (
                  <div className="naati-ai-settings-form">
                    <div className="naati-ai-settings-full naati-ai-openai-status">
                      <div className="naati-ai-openai-status-header">
                        <span>OpenAI connection</span>
                        <button
                          type="button"
                          className="mock-credits-btn mock-credits-btn-secondary naati-ai-openai-refresh"
                          onClick={loadOpenAIStatus}
                          disabled={openaiLoading}
                        >
                          {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number; className?: string }>, {
                            size: 14,
                            className: openaiLoading ? 'naati-ai-spin' : undefined,
                          })}
                          {openaiLoading ? 'Checking…' : 'Test connection'}
                        </button>
                      </div>
                      <div
                        className={`naati-ai-openai-banner ${
                          openaiStatus?.connected
                            ? 'naati-ai-openai-banner-ok'
                            : openaiStatus?.key_configured
                              ? 'naati-ai-openai-banner-warn'
                              : 'naati-ai-openai-banner-error'
                        }`}
                      >
                        {openaiStatus?.connected
                          ? React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 18 })
                          : React.createElement(FiAlertCircle as React.ComponentType<{ size?: number }>, { size: 18 })}
                        <div>
                          <strong>
                            {openaiStatus?.connected
                              ? 'Connected to OpenAI'
                              : openaiStatus?.key_configured
                                ? 'API key configured but not connected'
                                : 'API key not configured'}
                          </strong>
                          <p>{openaiStatus?.message || 'Click "Test connection" to verify your API key.'}</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="bot_name">Bot name</label>
                      <input
                        id="bot_name"
                        value={configForm.bot_name || ''}
                        onChange={(e) => setConfigForm({ ...configForm, bot_name: e.target.value })}
                      />
                    </div>
                    <div className="naati-ai-settings-check">
                      <label htmlFor="is_enabled">
                        <input
                          id="is_enabled"
                          type="checkbox"
                          checked={configForm.is_enabled ?? true}
                          onChange={(e) => setConfigForm({ ...configForm, is_enabled: e.target.checked })}
                        />
                        Enabled
                      </label>
                    </div>
                    <div className="naati-ai-settings-full">
                      <label htmlFor="welcome_message">Welcome message</label>
                      <textarea
                        id="welcome_message"
                        rows={3}
                        value={configForm.welcome_message || ''}
                        onChange={(e) => setConfigForm({ ...configForm, welcome_message: e.target.value })}
                      />
                    </div>
                    <div className="naati-ai-settings-full">
                      <label htmlFor="system_prompt_extra">Extra system prompt</label>
                      <textarea
                        id="system_prompt_extra"
                        rows={4}
                        value={configForm.system_prompt_extra || ''}
                        onChange={(e) => setConfigForm({ ...configForm, system_prompt_extra: e.target.value })}
                      />
                    </div>
                    <div className="naati-ai-settings-full">
                      <label htmlFor="escalation_hint">Escalation hint</label>
                      <textarea
                        id="escalation_hint"
                        rows={3}
                        value={configForm.escalation_hint || ''}
                        onChange={(e) => setConfigForm({ ...configForm, escalation_hint: e.target.value })}
                      />
                    </div>
                    <div>
                      <label htmlFor="model_name">Chat model</label>
                      {openaiStatus?.connected && chatModelOptions.length > 0 ? (
                        <select
                          id="model_name"
                          value={configForm.model_name || ''}
                          onChange={(e) => setConfigForm({ ...configForm, model_name: e.target.value })}
                        >
                          {chatModelOptions.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select id="model_name" disabled value={configForm.model_name || ''}>
                          <option value={configForm.model_name || ''}>{configForm.model_name || '—'}</option>
                        </select>
                      )}
                      {!openaiStatus?.connected && (
                        <p className="naati-ai-field-hint">Connect OpenAI to load available chat models.</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="embedding_model">Embedding model</label>
                      {openaiStatus?.connected && embeddingModelOptions.length > 0 ? (
                        <select
                          id="embedding_model"
                          value={configForm.embedding_model || ''}
                          onChange={(e) => setConfigForm({ ...configForm, embedding_model: e.target.value })}
                        >
                          {embeddingModelOptions.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select id="embedding_model" disabled value={configForm.embedding_model || ''}>
                          <option value={configForm.embedding_model || ''}>
                            {configForm.embedding_model || '—'}
                          </option>
                        </select>
                      )}
                      {!openaiStatus?.connected && (
                        <p className="naati-ai-field-hint">Connect OpenAI to load available embedding models.</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="max_context_messages">Context messages</label>
                      <input
                        id="max_context_messages"
                        type="number"
                        value={configForm.max_context_messages ?? 12}
                        onChange={(e) =>
                          setConfigForm({ ...configForm, max_context_messages: parseInt(e.target.value, 10) })
                        }
                      />
                    </div>
                    <div>
                      <label htmlFor="max_user_message_chars">Max message chars</label>
                      <input
                        id="max_user_message_chars"
                        type="number"
                        value={configForm.max_user_message_chars ?? 2000}
                        onChange={(e) =>
                          setConfigForm({ ...configForm, max_user_message_chars: parseInt(e.target.value, 10) })
                        }
                      />
                    </div>
                    <div>
                      <label htmlFor="rag_top_k">RAG top K</label>
                      <input
                        id="rag_top_k"
                        type="number"
                        value={configForm.rag_top_k ?? 4}
                        onChange={(e) => setConfigForm({ ...configForm, rag_top_k: parseInt(e.target.value, 10) })}
                      />
                    </div>
                    <div>
                      <label htmlFor="guest_hourly_limit">Guest hourly limit</label>
                      <input
                        id="guest_hourly_limit"
                        type="number"
                        value={configForm.guest_hourly_limit ?? 15}
                        onChange={(e) =>
                          setConfigForm({ ...configForm, guest_hourly_limit: parseInt(e.target.value, 10) })
                        }
                      />
                    </div>
                    <div>
                      <label htmlFor="user_hourly_limit">User hourly limit</label>
                      <input
                        id="user_hourly_limit"
                        type="number"
                        value={configForm.user_hourly_limit ?? 40}
                        onChange={(e) =>
                          setConfigForm({ ...configForm, user_hourly_limit: parseInt(e.target.value, 10) })
                        }
                      />
                    </div>
                    <div className="naati-ai-settings-check naati-ai-settings-full">
                      <label htmlFor="show_on_public_pages">
                        <input
                          id="show_on_public_pages"
                          type="checkbox"
                          checked={configForm.show_on_public_pages ?? true}
                          onChange={(e) =>
                            setConfigForm({ ...configForm, show_on_public_pages: e.target.checked })
                          }
                        />
                        Show on public pages (landing)
                      </label>
                    </div>
                    <button
                      type="button"
                      className="mock-credits-btn mock-credits-btn-primary naati-ai-save-btn"
                      onClick={handleSaveConfig}
                      disabled={saving}
                    >
                      {React.createElement(FiSave as React.ComponentType<{ size?: number }>, { size: 16 })}
                      {saving ? 'Saving…' : 'Save settings'}
                    </button>
                  </div>
                ) : (
                  <p className="mock-credits-tickets-empty">Loading settings…</p>
                )
              )}

              {activeTab === 'knowledge' && (
                <>
                  <div className="mock-credit-admin-toolbar">
                    <button type="button" className="mock-credits-btn mock-credits-btn-primary" onClick={openCreateArticle}>
                      {React.createElement(FiPlus as React.ComponentType<{ size?: number }>, { size: 16 })}
                      Add article
                    </button>
                    <button
                      type="button"
                      className="mock-credits-btn mock-credits-btn-secondary"
                      onClick={handleReindexAll}
                      disabled={reindexing}
                    >
                      {React.createElement(FiZap as React.ComponentType<{ size?: number }>, { size: 16 })}
                      {reindexing ? 'Reindexing…' : 'Reindex all embeddings'}
                    </button>
                    <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={loadKnowledge}>
                      {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number }>, { size: 16 })}
                      Refresh
                    </button>
                    <input
                      type="search"
                      className="mock-credit-admin-search"
                      placeholder="Search knowledge…"
                      value={knowledgeSearch}
                      onChange={(e) => setKnowledgeSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && loadKnowledge()}
                    />
                  </div>
                  {loading ? (
                    <p className="mock-credits-tickets-empty">Loading…</p>
                  ) : articles.length === 0 ? (
                    <p className="mock-credits-tickets-empty">No knowledge articles yet.</p>
                  ) : (
                    <div className="naati-ai-knowledge-panel">
                      <div className="naati-ai-knowledge-table-wrap">
                        <table className="mock-credit-admin-table naati-ai-knowledge-table">
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Category</th>
                            <th>Active</th>
                            <th>Embedding</th>
                            <th>Order</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {articles.map((art) => (
                            <tr key={art.id}>
                              <td className="naati-ai-knowledge-title" title={art.title}>
                                {art.title}
                              </td>
                              <td>{art.category}</td>
                              <td>{art.is_active ? 'Yes' : 'No'}</td>
                              <td>
                                <span
                                  className={`naati-ai-badge ${art.has_embedding ? 'naati-ai-badge-yes' : 'naati-ai-badge-no'}`}
                                >
                                  {art.has_embedding ? 'Ready' : 'Missing'}
                                </span>
                              </td>
                              <td>{art.sort_order}</td>
                              <td className="naati-ai-knowledge-actions-cell">
                                <div className="naati-ai-table-actions">
                                <button
                                  type="button"
                                  className="mock-credits-btn mock-credits-btn-secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditArticle(art);
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="mock-credits-btn mock-credits-btn-secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRefreshEmbedding(art.id);
                                  }}
                                >
                                  Embed
                                </button>
                                <button
                                  type="button"
                                  className="mock-credits-btn mock-credits-btn-secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteArticle(art.id);
                                  }}
                                  aria-label="Delete"
                                >
                                  {React.createElement(FiTrash2 as React.ComponentType<{ size?: number }>, { size: 14 })}
                                </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'conversations' && (
                <>
                  <div className="mock-credit-admin-toolbar">
                    <select value={convStatusFilter} onChange={(e) => setConvStatusFilter(e.target.value)}>
                      <option value="">All statuses</option>
                      <option value="active">Active</option>
                      <option value="escalated">Escalated</option>
                      <option value="closed">Closed</option>
                    </select>
                    <input
                      type="search"
                      className="mock-credit-admin-search"
                      placeholder="Search by email or message…"
                      value={convSearch}
                      onChange={(e) => setConvSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && loadConversations()}
                    />
                    <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={loadConversations}>
                      {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number }>, { size: 16 })}
                      Refresh
                    </button>
                  </div>
                  {loading ? (
                    <p className="mock-credits-tickets-empty">Loading…</p>
                  ) : conversations.length === 0 ? (
                    <p className="mock-credits-tickets-empty">No conversations yet.</p>
                  ) : (
                    <div className="mock-credit-admin-table-wrap">
                      <table className="mock-credit-admin-table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>User</th>
                            <th>Status</th>
                            <th>Messages</th>
                            <th>Updated</th>
                          </tr>
                        </thead>
                        <tbody>
                          {conversations.map((conv) => (
                            <tr key={conv.id} onClick={() => openConversation(conv.id)}>
                              <td>#{conv.id}</td>
                              <td>{conv.user_email || 'Guest'}</td>
                              <td>
                                <span className={`naati-ai-conv-status naati-ai-conv-status-${conv.status}`}>
                                  {conv.status}
                                </span>
                              </td>
                              <td>{conv.message_count}</td>
                              <td>{formatDate(conv.updated_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {showArticleForm && (
        <div
          className="mock-credits-modal-overlay"
          onClick={() => setShowArticleForm(false)}
          role="presentation"
        >
          <div
            className="mock-credits-modal mock-credits-modal-wide"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <div className="mock-credits-modal-header">
              <h3>{editingArticle ? 'Edit article' : 'New knowledge article'}</h3>
              <button type="button" className="mock-credits-modal-close" onClick={() => setShowArticleForm(false)}>
                {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 22 })}
              </button>
            </div>
            <div className="mock-credits-modal-body">
              <div className="mock-credits-form-group">
                <label>Title</label>
                <input
                  value={articleForm.title}
                  onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                />
              </div>
              <div className="mock-credits-form-row">
                <div className="mock-credits-form-group">
                  <label>Category</label>
                  <select
                    value={articleForm.category}
                    onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mock-credits-form-group">
                  <label>Sort order</label>
                  <input
                    type="number"
                    value={articleForm.sort_order}
                    onChange={(e) => setArticleForm({ ...articleForm, sort_order: e.target.value })}
                  />
                </div>
              </div>
              <div className="mock-credits-form-group">
                <label>Keywords (comma-separated)</label>
                <input
                  value={articleForm.keywords}
                  onChange={(e) => setArticleForm({ ...articleForm, keywords: e.target.value })}
                />
              </div>
              <div className="mock-credits-form-group">
                <label>Content</label>
                <textarea
                  rows={8}
                  value={articleForm.content}
                  onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                />
              </div>
              <div className="mock-credits-form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={articleForm.is_active}
                    onChange={(e) => setArticleForm({ ...articleForm, is_active: e.target.checked })}
                  />{' '}
                  Active
                </label>
              </div>
            </div>
            <div className="mock-credits-modal-footer">
              <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={() => setShowArticleForm(false)}>
                Cancel
              </button>
              <button type="button" className="mock-credits-btn mock-credits-btn-primary" onClick={handleSaveArticle} disabled={saving}>
                {saving ? 'Saving…' : 'Save & embed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedConv && (
        <div
          className="mock-credits-modal-overlay"
          onClick={() => setSelectedConv(null)}
          role="presentation"
        >
          <div
            className="mock-credits-modal mock-credits-modal-wide"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <div className="mock-credits-modal-header">
              <h3>
                Conversation #{selectedConv.id} — {selectedConv.user_email || 'Guest'}
              </h3>
              <button type="button" className="mock-credits-modal-close" onClick={() => setSelectedConv(null)}>
                {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 22 })}
              </button>
            </div>
            <div className="mock-credits-modal-body">
              <p style={{ marginBottom: '1rem', color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                Status:{' '}
                <span className={`naati-ai-conv-status naati-ai-conv-status-${selectedConv.status}`}>
                  {selectedConv.status}
                </span>
                {selectedConv.escalated_at && ` · Escalated ${formatDate(selectedConv.escalated_at)}`}
              </p>
              {selectedConv.escalation_note && (
                <div className="mock-credits-alert mock-credits-alert-info" style={{ marginBottom: '1rem' }}>
                  {selectedConv.escalation_note}
                </div>
              )}
              <div className="mock-credits-thread">
                {(selectedConv.messages || []).map((msg) => (
                  <div
                    key={msg.id}
                    className={`mock-credits-msg mock-credits-msg-${msg.role === 'user' ? 'user' : 'admin'}`}
                  >
                    <div className="mock-credits-msg-role">{msg.role}</div>
                    <div className="mock-credits-msg-body">{msg.content}</div>
                    <div className="mock-credits-msg-time">{formatDate(msg.created_at)}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mock-credits-modal-footer">
              {selectedConv.status !== 'closed' && (
                <button
                  type="button"
                  className="mock-credits-btn mock-credits-btn-secondary"
                  onClick={() => handleCloseConversation(selectedConv.id)}
                >
                  Close conversation
                </button>
              )}
              <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={() => setSelectedConv(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportAIManagement;
